import React, {FC, useState} from "react";
import {ModuleList} from "../../components/module-list";
import {Button} from "../../../../lib/src/components/form/Button";
import {useApiCall, useApiEndpoint} from "../../../../lib/src/api";
import {config} from "../../config";
import {CategoryList} from "../../components/category-list";
import {useNavigate, useParams} from "react-router-dom";
import {ContentContainer} from "../../../../lib/src/components/content/ContentContainer";
import {faChevronLeft, faPlus} from "@fortawesome/free-solid-svg-icons";
import {AddLearnCategoryModal} from "../../modals/AddLearnCategoryModal";
import {LearnIcon} from "./LearnIcon";
import {Breadcrumbs} from "../../../../lib/src/components/content/Breadcrumbs";
import {AddLearnModuleModal} from "../../modals/AddLearnModuleModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {LearnCategory, LearnModule} from "../../../../lib/src/api/repositories/learn-repository";
import {faEllipsisVertical} from "@fortawesome/pro-solid-svg-icons";
import {CategoryContextMenu} from "../../components/learn/CategoryContextMenu";
import {ModuleContextMenu} from "../../components/learn/ModuleContextMenu";
import {useModal} from "@jane/lib/src/components/layout/ModalProvider";

export const LearnCategoryDetailsPage: FC = () => {
  const { learn } = useApiCall(config)
  const { categoryId } = useParams()
  const navigate = useNavigate()
  const {resource: moduleResource, reload: reloadModules} = useApiEndpoint(() => learn.listModules())
  const { resource: categoryResource, reload: reloadCategories} = useApiEndpoint(() => learn.listCategories())
  const category = categoryResource?.find((c) => c.id === categoryId) // current category
  const parentCategory = categoryResource?.find((c) => category?.parent_category_id === c.id)
  const subCategories = categoryResource?.filter((c) => c.parent_category_id === categoryId).sort((a, b) => a.name.localeCompare(b.name));
  const relatedModules = moduleResource?.filter((m) => m.category_id === categoryId).sort((a, b) => a.name.localeCompare(b.name));
  return <ContentContainer>
    <div>
      {category === undefined ? <></> : <div className={'flex space-x-2 items-center'}>
        <FontAwesomeIcon icon={faChevronLeft} className={'text-3xl px-4 py-3 2xl:ml-[-60px] hover:bg-brand-200 hover:text-brand-700 rounded-full hover:cursor-pointer'}
                         onClick={() => {category.parent_category_id ? navigate(`/learn/categories/${category.parent_category_id}`) : navigate(`/learn`)}}/>
        <LearnIcon icon={category.icon} className={'mr-2 text-4xl bg-brand-200 text-brand-700 px-3 py-3 rounded-full'} />
        {/* mt-4 to offset the breadcrumbs margin */}
        <div className={'flex flex-col space-y-2 mt-4'}>
          <Breadcrumbs currentPage={category?.name ?? ''} crumbs={[
            {label: 'Kennisbank', href: '/learn'},
            ...(parentCategory ? [{ label: parentCategory.name, href: `/learn/categories/${parentCategory.id}` }] : [])
          ]} />
          <div className={'text-2xl font-medium'}>{category.name}</div>
        </div>
      </div>}
    </div>

    {category && subCategories && categoryResource &&
      <SubCategories parentCategory={category} categories={subCategories} allCategories={categoryResource} reloadCategories={reloadCategories} /> }

    {category && relatedModules && categoryResource &&
      <Modules categories={categoryResource} parentCategory={category} modules={relatedModules} reloadModules={reloadModules} />}

  </ContentContainer>
}

const SubCategories: FC<{ parentCategory: LearnCategory|null, categories: LearnCategory[], allCategories: LearnCategory[], reloadCategories: () => void}> = (props) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const { open: addSubCategoryModal } = useModal({title: "Sub-categorie toevoegen", body: <AddLearnCategoryModal parentCategory={props.parentCategory ?? null} isPinned={false} onFinished={props.reloadCategories} />, size: "xl"})
  return <>
    <section className={'my-8'}>
      <div className={'flex justify-between py-2'}>
        <div className={'text-xl font-medium'}>Vastgezette categorieën</div>
        <div className={'flex space-x-4'}>
          <Button type={'secondary'} size={'xs'} text={'Toevoegen'} icon={faPlus} onClick={addSubCategoryModal} />
          {selectedCategories.length > 0 && <div className={'relative'}>
            <Button type={'primary'} size={'xs'} text={`Acties (${selectedCategories.length})`} icon={faEllipsisVertical} onClick={() => setContextMenuOpen(true)} />
            <CategoryContextMenu selected={selectedCategories} setSelected={setSelectedCategories} categories={props.allCategories} open={contextMenuOpen} setOpen={setContextMenuOpen} reload={props.reloadCategories} />
          </div>}
        </div>
      </div>
      <div className={''}>
        <CategoryList categories={props.categories} allCategories={props.allCategories} reload={props.reloadCategories} selected={selectedCategories} setSelected={setSelectedCategories} />
      </div>
    </section>
  </>
}

const Modules: FC<{ categories: LearnCategory[], parentCategory: LearnCategory|null, modules: LearnModule[], reloadModules: () => void}> = (props) => {
  const [selectedModules, setSelectedModules] = useState<string[]>([])
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const { open: addModuleModal } = useModal({title: "Module toevoegen", body: <AddLearnModuleModal parentCategory={props.parentCategory ?? null} onFinished={props.reloadModules} />, size: "md"})
  return <>
    <section className={'my-8'}>
      <div className={'flex justify-between py-2'}>
        <div className={'text-xl font-medium'}>Alle modules</div>
        <div className={'flex space-x-4'}>
          <Button type={'secondary'} size={"xs"} text={'Toevoegen'} icon={faPlus} onClick={addModuleModal} />
          {selectedModules.length > 0 && <div className={'relative'}>
            <Button type={'primary'} size={'xs'} text={`Acties (${selectedModules.length})`} icon={faEllipsisVertical} onClick={() => setContextMenuOpen(true)} />
            <ModuleContextMenu selected={selectedModules} setSelected={setSelectedModules} modules={props.modules} open={contextMenuOpen} setOpen={setContextMenuOpen} reload={props.reloadModules}/>
          </div>}
        </div>
      </div>
      <div className={''}>
        <ModuleList categories={props.categories} modules={props.modules} reload={props.reloadModules} selected={selectedModules} setSelected={setSelectedModules} />
      </div>
    </section>
  </>
}